import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  input,
  OnDestroy,
  output,
  Output,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { delay, distinctUntilChanged, finalize, map, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import {
  AbstractPaymentMethod,
  PaymentFlow,
  PaymentMethodIntent,
  PaymentMethodMode,
  PaymentPlatformService,
  StripeSelectPaymentMethodComponent,
} from '../../platforms';
import { BehaviorSubject, combineLatest, merge, Observable, of, Subject } from 'rxjs';
import { emitValueIfValid, getFirstOfQueryList, ignoreFalsy, ignoreNullish, ignoreTruthy, Logger, Validate } from '@aiwyn/shared';
import { AuthAdaptorService } from '@aiwyn/auth';
import { PaymentMethodType, PaymentPlatform } from '@aiwyn/api';
import { ConfigService } from '@aiwyn/config';
import {
  NewPaymentMethodDialogComponent,
  NewPaymentMethodDialogData,
  NewPaymentMethodDialogRef,
  NewPaymentMethodDialogResult,
} from '../new-payment-method-dialog/new-payment-method-dialog.component';
import {
  UpdateBillingAddressDialogComponent,
  UpdateBillingAddressDialogData,
  UpdateBillingAddressDialogRef,
} from '../update-billing-address-dialog/update-billing-address-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { PaymentsState } from '../../state';
import {
  AutoPaymentPlanTrackService,
  AutoPaymentPlanWorkflowEvent,
  PaymentIntentTrigger,
  PaymentMethodSurface,
  PaymentPlanTrackService,
  PaymentPlanWorkflowEvent,
  PaymentsWorkflowType,
  PaymentTrackService,
  PaymentWorkflowEvent,
} from '../../tracking';

/**
 * Workflows whose taxonomy includes a `*PAYMENT_METHOD_SELECTED` step. The
 * SelectPaymentMethodComponent dispatches that step on behalf of its host so callers
 * don't have to wire their own (paymentMethodSelected) tracking — pass the workflow
 * the component is participating in via the `workflow` input.
 */
export type SelectPaymentMethodWorkflow =
  | PaymentsWorkflowType.PAYMENT
  | PaymentsWorkflowType.PAYMENT_PLAN
  | PaymentsWorkflowType.AUTO_PAYMENT_PLAN;

type PaymentPlatformTemplate = StripeSelectPaymentMethodComponent; // | OtherPaymentPlatformSelectPaymentMethodComponent;

@Component({
  selector: 'app-select-payment-method',
  templateUrl: './select-payment-method.component.html',
  styleUrls: ['./select-payment-method.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectPaymentMethodComponent implements Validate, AfterViewInit, OnDestroy {
  logger = inject(Logger);
  dialog = inject(MatDialog);
  // Each workflow owns its own tracker; the host component that mounts this
  // SelectPaymentMethodComponent provides the matching one. `optional: true` keeps the
  // component injectable in hosts that participate in only one of the three workflows —
  // e.g. an AutoPay dialog never provides PaymentPlanTrackService and vice versa.
  private readonly paymentTrackService = inject(PaymentTrackService, { optional: true });
  private readonly paymentPlanTrackService = inject(PaymentPlanTrackService, { optional: true });
  private readonly autoPaymentPlanTrackService = inject(AutoPaymentPlanTrackService, { optional: true });

  /**
   * Used to specify payment method to be selected on initialization. Should NOT be used for any other purpose.
   */
  intent$ = new BehaviorSubject<PaymentMethodIntent>(undefined);
  @Input() set intent(intent: PaymentMethodIntent) {
    emitValueIfValid(intent, this.intent$);
  }

  /**
   * If intent is 'payment', this will be used to determine the payment flow.
   */
  paymentFlow$ = new BehaviorSubject<PaymentFlow>(undefined);
  @Input() set paymentFlow(paymentFlow: PaymentFlow) {
    emitValueIfValid(paymentFlow, this.paymentFlow$);
  }

  /**
   * Used to specify payment method to be selected on initialization. Should NOT be used for any other purpose.
   */
  selectedPaymentMethodId$ = new BehaviorSubject<string>(undefined);
  @Input() set selectedPaymentMethodId(selectedPaymentMethodId: string) {
    emitValueIfValid(selectedPaymentMethodId, this.selectedPaymentMethodId$);
  }

  /**
   * If intent is 'payment', this will be used to determine the payment flow.
   */
  paymentMethodMode$ = new BehaviorSubject<PaymentMethodMode>(undefined);
  @Input() set paymentMethodMode(paymentMethodMode: PaymentMethodMode) {
    emitValueIfValid(paymentMethodMode, this.paymentMethodMode$);
  }

  /**
   * If intent is 'payment', this will be used to determine the payment flow.
   */
  canSetDefaultPaymentMethodMode$ = new BehaviorSubject<boolean>(undefined);
  @Input() set canSetDefaultPaymentMethodMode(canSetDefaultPaymentMethodMode: boolean) {
    emitValueIfValid(canSetDefaultPaymentMethodMode, this.canSetDefaultPaymentMethodMode$);
  }

  /**
   * This will only be true in the engagement wizard's payment info step
   * when there is no prepayment to be made and adding a payment method to the engagement is optional.
   */
  setUpLaterEnabled$ = new BehaviorSubject<boolean>(false);
  @Input() set setUpLaterEnabled(setUpLaterEnabled: boolean) {
    emitValueIfValid(setUpLaterEnabled, this.setUpLaterEnabled$);
  }

  /**
   * Workflow the component is participating in. Drives which `*PAYMENT_METHOD_SELECTED`
   * step is emitted when the user selects a payment method, so the host doesn't need to
   * (and shouldn't) wire its own duplicate (paymentMethodSelected) tracking.
   */
  workflow = input<SelectPaymentMethodWorkflow>(PaymentsWorkflowType.PAYMENT);

  /**
   * `PaymentMethodSurface` the host is exposing. Forwarded into the
   * `NewPaymentMethodDialogComponent` / `UpdateBillingAddressDialogComponent` openers so
   * the PAYMENT_METHOD_* taxonomy can attribute add/manage events to the host context. Hosts
   * with a pathname-derived surface (PaymentsSidebarComponent, OpenPortalComponent) bind this
   * via `PaymentsState.resolvePaymentMethodSurfaceFromUrl()`; hosts with a fixed surface
   * (payment-plan-modal, autopay-dialog, engagement-payment-info-step) bind the literal.
   */
  surface = input.required<PaymentMethodSurface>();

  /**
   * Lets a host suppress the built-in "Payment method" title when it renders its own section header (the
   * email-first hosts group email + methods under a single "Payment Information" heading). Defaults to true
   * so every existing consumer is unchanged.
   */
  showHeader = input(true);

  /**
   * Email-first gate over new-payment-method creation only. `emailGateActive` = the flag is on;
   * `emailGateLocked` = no valid email yet. Both are forwarded to the Stripe selector, which gates the
   * creation area and leaves saved methods selectable. lockedNewMethodAttempt fires when the payer clicks
   * the locked creation area, so the host can focus the email field. Defaults keep other consumers unchanged.
   */
  emailGateActive = input(false);
  emailGateLocked = input(false);
  /** The payer's email was prepopulated on load — forwarded to suppress the gate status banner. */
  emailGatePrepopulated = input(false);
  lockedNewMethodAttempt = output<void>();

  @Output() paymentMethodModeChange = new EventEmitter<PaymentMethodMode>();
  @Output() paymentMethodSelected = new EventEmitter<AbstractPaymentMethod>();

  private readonly _unsubscribe$ = new Subject<void>();
  @ViewChildren('stripeSelectPaymentMethodTemplate', { read: TemplateRef })
  stripeSelectPaymentMethodTemplate: QueryList<TemplateRef<StripeSelectPaymentMethodComponent>>;

  private readonly _ngAfterViewInit$ = new Subject<void>();

  paymentPlatformSelectPaymentMethodTemplate$ = this._ngAfterViewInit$.pipe(
    switchMap(() => this.paymentPlatformService.platform),
    distinctUntilChanged(),
    switchMap(paymentPlatform => getFirstOfQueryList(this._marshalPaymentPlatformTemplate(paymentPlatform))),
    delay(1), // ugly ExpressionChangedAfterItHasBeenCheckedError workaround
  );

  setDefaultPaymentMethodMode$ = this.canSetDefaultPaymentMethodMode$.pipe(
    ignoreFalsy(),
    switchMap(_ => this.paymentMethodMode$),
    take(1),
    // payment method mode already provided, no need to set a default that would override it
    ignoreTruthy(),
    switchMap(_ => {
      if (!this.authAdaptor.isContactOrAdmin) {
        return this.paymentPlatformService.getTemporaryPaymentMethod().pipe(
          map(temporaryPaymentMethod => temporaryPaymentMethod == undefined ? undefined : PaymentMethodMode.TEMPORARY),
        );
      }

      return this.paymentPlatformService.getSavedPaymentMethodsExist().pipe(
        map(savedPaymentMethodsExist => savedPaymentMethodsExist ? PaymentMethodMode.SAVED : undefined),
      );
    }),
    take(1),
    ignoreNullish(),
    tap(paymentMethodMode => this.paymentMethodModeChange.emit(paymentMethodMode)),
    takeUntil(this._unsubscribe$),
  );

  cardRestrictionsHint$ = this.paymentPlatformService.getCardRestrictionsHint();
  paymentMethodValidationErrorMessage$ = new BehaviorSubject<string>(undefined);

  /**
   * The (paymentMethodSelected) output is intentionally suppressed by stripe-select-saved-payment-method when a
   * method is already selected on entry, so onPaymentMethodSelected never tracks the prepopulated case. This one-shot
   * listener resolves the prepopulated id and tracks it once on entry, then completes; subsequent user-driven changes
   * are tracked via onPaymentMethodSelected. takeUntil(paymentMethodSelected) closes the listener if a user-driven
   * emission lands before getSavedPaymentMethod resolves, so we don't double-track.
   */
  trackInitialPrepopulatedPaymentMethod$ = this.selectedPaymentMethodId$.pipe(
    ignoreNullish(),
    take(1),
    switchMap(initialSelectedPaymentMethodId => this.paymentPlatformService.getSavedPaymentMethod(initialSelectedPaymentMethodId)),
    take(1),
    // PP-3775: the prepopulated method is auto-selected on entry, not chosen by the user.
    tap(paymentMethod => this._trackPaymentMethodSelected(paymentMethod, 'auto')),
    takeUntil(merge(this.paymentMethodSelected, this._unsubscribe$)),
  );

  constructor(
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly authAdaptor: AuthAdaptorService,
    private readonly configService: ConfigService,
    private readonly store: Store,
  ) {
    this.setDefaultPaymentMethodMode$.subscribe();
    this.trackInitialPrepopulatedPaymentMethod$.subscribe();
  }

  ngAfterViewInit(): void {
    this._ngAfterViewInit$.next();
  }

  onPaymentMethodSelected(paymentMethod: AbstractPaymentMethod): void {
    this.paymentMethodValidationErrorMessage$.next(undefined);
    this.paymentMethodSelected.emit(paymentMethod);
    this._trackPaymentMethodSelected(paymentMethod);
  }

  private _trackPaymentMethodSelected(
    paymentMethod: AbstractPaymentMethod | undefined,
    // PP-3775: defaults to 'user' so the user-driven onPaymentMethodSelected path is tagged as intent
    // without a change at its call site; the prepopulated-on-entry path passes 'auto'.
    intentTrigger: PaymentIntentTrigger = 'user',
  ): void {
    if (!paymentMethod?.type) {
      return;
    }

    const workflow = this.workflow();
    const paymentMethodType = (paymentMethod.subType ?? paymentMethod.type ?? '').toString().toUpperCase();

    // Only the payment workflow's method-selected event carries the common envelope today.
    // Payment-plan / auto-payment-plan flavors keep the minimal shape until their event maps are
    // refined in a follow-up. Dispatch through the tracker matching the workflow input — the
    // host component provides exactly one of them; the others stay null.
    if (workflow === PaymentsWorkflowType.PAYMENT) {
      this.paymentTrackService?.trackWorkflow(
        PaymentWorkflowEvent.PAYMENT_METHOD_SELECTED,
        { ...PaymentsState.buildPaymentEventBase(this.store, paymentMethod), intentTrigger },
      );
      return;
    }

    if (workflow === PaymentsWorkflowType.PAYMENT_PLAN) {
      this.paymentPlanTrackService?.trackWorkflow(
        PaymentPlanWorkflowEvent.PAYMENT_PLAN_PAYMENT_METHOD_SELECTED,
        { paymentMethodType },
      );
      return;
    }

    if (workflow === PaymentsWorkflowType.AUTO_PAYMENT_PLAN) {
      this.autoPaymentPlanTrackService?.trackWorkflow(
        AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_PAYMENT_METHOD_SELECTED,
        { paymentMethodType },
      );
    }
  }

  newPaymentMethodDialog: NewPaymentMethodDialogRef;
  // `newPaymentMethodDialog` is only assigned once the async pipeline below emits, so it alone cannot guard against rapid
  // repeat clicks — two clicks in quick succession opened two stacked dialogs, whose duplicate Stripe elements then threw
  // IntegrationError (PP-3625, PP-3574). This flag closes that window synchronously.
  private _isNewPaymentMethodDialogPipelineActive = false;
  openNewPaymentMethodDialog(selectedPaymentMethodType?: PaymentMethodType): void {
    // While the email-first gate is locked, block new-method creation from every entry point
    // (the new-method-type options handle this themselves; this also covers "Edit payment method" and any
    // other caller). Saved-method selection does not go through here, so it stays unlocked.
    if (this.emailGateActive() && this.emailGateLocked()) {
      this.lockedNewMethodAttempt.emit();
      return;
    }

    if (this._isNewPaymentMethodDialogPipelineActive || this.newPaymentMethodDialog != undefined) {
      this.logger.warn('PAYMENT : SelectPaymentMethodComponent : openNewPaymentMethodDialog : Dialog already open. Ignoring click.');
      return;
    }
    this._isNewPaymentMethodDialogPipelineActive = true;

    (selectedPaymentMethodType ? this.paymentPlatformService.setNewPaymentMethodType(selectedPaymentMethodType) : of(undefined)).pipe(
      switchMap(_ => combineLatest([
        this.intent$,
        this.paymentFlow$,
        this.paymentPlatformService.getSavedPaymentMethodsExist(),
        this.paymentMethodMode$,
      ])),
      take(1),
      switchMap(([intent, paymentFlow, userHasSavedPaymentMethods, existingPaymentMethodMode]) => {
        this.logger.debug(
          'PAYMENT : SelectPaymentMethodComponent : openNewPaymentMethodDialog : Opening dialog.',
          'intent =', intent,
          'paymentFlow =', paymentFlow,
          'userHasSavedPaymentMethods =', userHasSavedPaymentMethods,
          'existingPaymentMethodMode =', existingPaymentMethodMode,
          'selectedPaymentMethodType =', selectedPaymentMethodType,
        );
        this.newPaymentMethodDialog = this.dialog.open<NewPaymentMethodDialogComponent, NewPaymentMethodDialogData>(
          NewPaymentMethodDialogComponent,
          { data: { userHasSavedPaymentMethods, intent, paymentFlow, surface: this.surface() } },
        );

        return this.newPaymentMethodDialog.afterClosed().pipe(
          // clean up and set temporary payment method if necessary
          switchMap(newPaymentMethodDialogResult => combineLatest([
            this.paymentPlatformService.removeNewBillingDetails(),
            this.setTemporaryPaymentMethodIfApplicable(newPaymentMethodDialogResult),
          ]).pipe(
            // after processing has completed, emit the determined payment method mode and created payment method
            tap(_ => {
              if (newPaymentMethodDialogResult?.createdPaymentMethod != undefined) {
                this.paymentMethodSelected.emit(newPaymentMethodDialogResult.createdPaymentMethod);
                this._trackPaymentMethodSelected(newPaymentMethodDialogResult.createdPaymentMethod);
              }

              const paymentMethodMode = this.determinePaymentMethodMode(newPaymentMethodDialogResult, existingPaymentMethodMode);
              this.paymentMethodModeChange.emit(paymentMethodMode);
            }),
          )),
        );
      }),
      take(1),
      finalize(() => {
        this.newPaymentMethodDialog = undefined;
        this._isNewPaymentMethodDialogPipelineActive = false;
      }),
    ).subscribe();
  }

  updateBillingAddressDialog: UpdateBillingAddressDialogRef;
  openUpdateBillingAddressDialog(paymentMethod: AbstractPaymentMethod): void {
    if (this.updateBillingAddressDialog != undefined) {
      return;
    }

    this.updateBillingAddressDialog = this.dialog.open<UpdateBillingAddressDialogComponent, UpdateBillingAddressDialogData>(
      UpdateBillingAddressDialogComponent,
      { data: { paymentMethod, surface: this.surface() } },
    );

    this.updateBillingAddressDialog.afterClosed().pipe(
      take(1),
      switchMap(_ => this.paymentPlatformService.removeNewBillingDetails()),
      finalize(() => this.updateBillingAddressDialog = undefined),
    ).subscribe();
  }

  determinePaymentMethodMode(
    newPaymentMethodDialogResult: NewPaymentMethodDialogResult,
    existingPaymentMethodMode: PaymentMethodMode,
  ): PaymentMethodMode {
    // if the user cancels the dialog, go back to previous payment method mode
    if (newPaymentMethodDialogResult == undefined) {
      return existingPaymentMethodMode;
    }

    return newPaymentMethodDialogResult.isTemporary ? PaymentMethodMode.TEMPORARY : PaymentMethodMode.SAVED;
  }

  setTemporaryPaymentMethodIfApplicable(newPaymentMethodDialogResult: NewPaymentMethodDialogResult): Observable<unknown> {
    if (!newPaymentMethodDialogResult?.isTemporary) {
      return of(undefined);
    }

    return this.paymentPlatformService.setTemporaryPaymentMethod(newPaymentMethodDialogResult.createdPaymentMethod.model);
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public validate(): Observable<boolean> {
    return this.selectedPaymentMethodId$.pipe(
      withLatestFrom(this.paymentMethodMode$),
      switchMap(([selectedPaymentMethodId, paymentMethodMode]) => {
        if (paymentMethodMode === PaymentMethodMode.SET_UP_LATER) {
          return of(undefined as string);
        }

        return (
          paymentMethodMode === PaymentMethodMode.TEMPORARY
            ? this.paymentPlatformService.getTemporaryPaymentMethod()
            : this.paymentPlatformService.getSavedPaymentMethod(selectedPaymentMethodId)
        ).pipe(
          take(1),
          map(selectedPaymentMethod => {
            if (this.configService.levelThreeDataEnabled && selectedPaymentMethod?.billingDetailsIncomplete) {
              return `Please update the billing address for the card ending in ${selectedPaymentMethod.lastFour}.`;
            }

            if (selectedPaymentMethod?.isValid) {
              return undefined;
            }

            return selectedPaymentMethod == undefined ? 'A payment method is required' : selectedPaymentMethod.invalidMessage;
          }),
        );
      }),
      tap(paymentMethodValidationErrorMessage => this.paymentMethodValidationErrorMessage$.next(paymentMethodValidationErrorMessage)),
      map(validationErrorMessage => validationErrorMessage == undefined),
    );
  }

  private _marshalPaymentPlatformTemplate(paymentPlatform: PaymentPlatform): QueryList<TemplateRef<PaymentPlatformTemplate>> {
    switch (paymentPlatform) {
      case PaymentPlatform.STRIPE:
        return this.stripeSelectPaymentMethodTemplate;
      default:
        throw new Error(`No payment platform select payment method template for ${paymentPlatform}`);
    }
  }
}
